import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError(false);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };
  
  return (
    <div className="contact-container">
      <section className="page-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Have questions or need support? We're here to help.</p>
        </div>
      </section>
    
      <section className="contact-main">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <p>
                We'd love to hear from you. Whether you have a question about our platform, 
                pricing, or anything else, our team is ready to answer all your questions.
              </p>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="method-details">
                    <h3>Our Location</h3>
                    <p>123 Sports Avenue, Suite 400<br />Denver, CO 80202</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon">
                    <Phone size={24} />
                  </div>
                  <div className="method-details">
                    <h3>Phone Number</h3>
                    <p>(303) 555-2468</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div className="method-details">
                    <h3>Email</h3>
                    <p>support@playmakerpics.com</p>
                  </div>
                </div>
              </div>
              
              <div className="business-hours">
                <h3>Business Hours</h3>
                <p>Monday - Friday: 9am - 5pm MST<br />Saturday & Sunday: Closed</p>
              </div>
            </div>
            
            <div className="contact-form-container">
              <h2>Send a Message</h2>
              
              {submitSuccess && (
                <div className="success-message">
                  Your message has been sent successfully. We'll get back to you soon!
                </div>
              )}
              
              {submitError && (
                <div className="error-message">
                  There was an error sending your message. Please try again.
                </div>
              )}
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    value={formData.subject}
                    onChange={handleChange}
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="6" 
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="loading-text">
                      <span className="spinner"></span>
                      Sending...
                    </span>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      <section className="map-section">
        <div className="container">
          <div className="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d196281.12937408483!2d-104.99518559872268!3d39.764333694062775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876b80aa231f17cf%3A0x118ef4f8278a36d6!2sDenver%2C%20CO!5e0!3m2!1sen!2sus!4v1621436478244!5m2!1sen!2sus" 
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy"
              title="PlayMaker Pics office location"
            ></iframe>
          </div>
        </div>
      </section>
      
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I get started with PlayMaker Pics?</h3>
              <p>Simply create an account, choose a subscription plan that fits your needs, and start uploading your sports photos to our platform.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I try before I buy?</h3>
              <p>Yes! We offer a 14-day free trial that gives you full access to all features so you can see if PlayMaker Pics is right for you.</p>
            </div>
            
            <div className="faq-item">
              <h3>Do you offer team discounts?</h3>
              <p>We offer special pricing for schools, leagues, and teams. Contact our sales team for custom pricing tailored to your organization's needs.</p>
            </div>
            
            <div className="faq-item">
              <h3>What file formats are supported?</h3>
              <p>We support all major image formats including JPG, PNG, HEIC, and RAW files from most major camera manufacturers.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 